import { useCallback, useContext, useMemo } from 'react';
import { ContactFormConnector } from '../../../../services/connectors/ContactFormConnector';
import { AppShopContext } from '../../../context/AppShopContext';
import Order, { OrderArticle, OrderSellerData } from '../model/Order';
import { getLanguageFromLocale } from '../../../../utils/LocaleUtils';

export interface UseOrderQueriesResult {
  getOrders: (page: number, pageSize: number) => Promise<Order[]>;
  getOrderSellerData: (orderId: string, suborderId: string) => Promise<OrderSellerData>;
  getOrderArticles: (orderId: string, suborderId: string) => Promise<OrderArticle[]>;
}

export function useOrderQueries(): UseOrderQueriesResult {
  const appShopContext = useContext(AppShopContext);

  const connector = useMemo(() => new ContactFormConnector(), []);

  const getOrders = useCallback(
    async (page: number, pageSize: number) => {
      const orders = await connector.getOrdersV2(page, pageSize, appShopContext.siteId);
      return orders.flatMap((order) => {
        return order.suborders.map(
          (suborder) => new Order(suborder.id, order.creationDate, order.orderId),
        );
      });
    },
    [appShopContext.siteId, connector],
  );

  const getOrderArticles = useCallback(
    async (orderId: string, suborderId: string) => {
      const language = getLanguageFromLocale(appShopContext.locale);
      const response = await connector.getOrderArticlesV2(
        orderId,
        suborderId,
        language,
        appShopContext.locale,
        appShopContext.siteId,
      );
      return response.map(
        ({ productId, name, pictureUrl }) => new OrderArticle(productId, name, pictureUrl, true),
      );
    },
    [appShopContext.siteId, appShopContext.locale, connector],
  );

  const getOrderSellerData = useCallback(
    async (orderId: string, suborderId: string) => {
      const response = await connector.getOrderSellerDataV2(
        orderId,
        suborderId,
        appShopContext.siteId,
      );
      return new OrderSellerData(response.sellerName, response.orderType);
    },
    [connector, appShopContext.realm, appShopContext.siteId],
  );

  return useMemo(
    () => ({ getOrders, getOrderArticles, getOrderSellerData }),
    [getOrders, getOrderArticles, getOrderSellerData],
  );
}
